import { Button, Input, Select } from "@mixitone/components";
import ApplicationController, { ApplicationView } from "@mixitone/mvc";
import type React from "react";

export interface DivisionSplittingRule {
  positions: number[];
  division: number;
}

export interface DivisionSplittingRules {
  rules: DivisionSplittingRule[];
}

interface DivisionSplittingRulesFieldProps {
  value: string;
  onChange: (value: string) => void;
  divisions: number;
  qualifiersPerGroup: number;
}

interface State {
  rules: DivisionSplittingRule[];
  divisions: number;
  qualifiersPerGroup: number;
}

class DivisionSplittingRulesController extends ApplicationController<
  State,
  DivisionSplittingRulesFieldProps
> {
  // Keep a reference to the latest props so actions can call onChange
  private currentProps: DivisionSplittingRulesFieldProps | null = null;

  get initialState(): State {
    return {
      rules: [],
      divisions: 1,
      qualifiersPerGroup: 1,
    };
  }

  override async initialize(props: DivisionSplittingRulesFieldProps) {
    await this.changeProps(props);
  }

  override async changeProps(props: DivisionSplittingRulesFieldProps) {
    this.currentProps = props;
    const { divisions, qualifiersPerGroup, value } = props;

    this.state.divisions = divisions || 1;
    this.state.qualifiersPerGroup = qualifiersPerGroup || 1;

    // Parse and normalise rules from the incoming value
    const parsedRules = this.parseRules(value);
    const normalisedRules = this.normaliseRules(parsedRules);

    // Only update if rules have actually changed
    if (JSON.stringify(this.state.rules) !== JSON.stringify(normalisedRules)) {
      this.state.rules = normalisedRules;
    }
  }

  private parseRules(value: string): DivisionSplittingRule[] {
    if (!value) return [];
    try {
      const parsed = JSON.parse(value) as DivisionSplittingRules;
      if (!parsed || !Array.isArray(parsed.rules)) return [];
      return parsed.rules;
    } catch {
      return [];
    }
  }

  private normaliseRules(rules: DivisionSplittingRule[]): DivisionSplittingRule[] {
    const maxPos = this.state.qualifiersPerGroup || 1;
    const maxDiv = this.state.divisions || 1;

    return rules
      .map((rule) => ({
        division: Math.min(Math.max(rule.division || 1, 1), maxDiv),
        positions: (rule.positions || []).filter((p) => p >= 1 && p <= maxPos),
      }))
      .filter((rule) => rule.positions.length > 0);
  }

  private syncToProps() {
    // Filter out rules with empty positions when syncing to props (don't save incomplete rules)
    const completeRules = this.state.rules.filter((rule) => rule.positions.length > 0);
    const json = JSON.stringify({ rules: completeRules });
    const props = this.currentProps;
    if (!props) return;
    // Avoid redundant updates
    if (json !== (props.value || "")) {
      props.onChange(json);
    }
  }

  actionAddRule() {
    // Add a new rule without normalizing, so empty positions are allowed (user will fill them in)
    this.state.rules = [...this.state.rules, { positions: [], division: 1 }];
    // Don't sync to props yet since the rule is incomplete
  }

  actionRemoveRule(index: number) {
    const next = this.state.rules.filter((_, i) => i !== index);
    this.state.rules = this.normaliseRules(next);
    this.syncToProps();
  }

  actionUpdateRule(index: number, updates: Partial<DivisionSplittingRule>) {
    const next = [...this.state.rules];
    const updatedRule = {
      ...next[index],
      ...updates,
    };
    // Validate positions and division without filtering out rules with empty positions
    const maxPos = this.state.qualifiersPerGroup || 1;
    const maxDiv = this.state.divisions || 1;
    next[index] = {
      division: Math.min(Math.max(updatedRule.division || 1, 1), maxDiv),
      positions: (updatedRule.positions || []).filter((p) => p >= 1 && p <= maxPos),
    };
    this.state.rules = next;
    this.syncToProps();
  }

  actionUpdatePositions(index: number, positionsStr: string) {
    // Parse comma-separated positions (e.g., "1,2,3" or "1-4")
    const positions: number[] = [];
    const parts = positionsStr.split(",").map((p) => p.trim());
    for (const part of parts) {
      if (!part) continue;
      if (part.includes("-")) {
        const [start, end] = part.split("-").map((n) => Number.parseInt(n.trim(), 10));
        if (!Number.isNaN(start) && !Number.isNaN(end) && start <= end) {
          for (let i = start; i <= end; i++) {
            positions.push(i);
          }
        }
      } else {
        const num = Number.parseInt(part, 10);
        if (!Number.isNaN(num)) {
          positions.push(num);
        }
      }
    }
    const sorted = Array.from(new Set(positions)).sort((a, b) => a - b);
    this.actionUpdateRule(index, { positions: sorted });
  }

  getPositionsDisplay(positions: number[]): string {
    if (!positions || positions.length === 0) return "";
    if (positions.length === 1) return positions[0].toString();

    // Group consecutive positions
    const groups: number[][] = [];
    let currentGroup: number[] = [positions[0]];

    for (let i = 1; i < positions.length; i++) {
      if (positions[i] === positions[i - 1] + 1) {
        currentGroup.push(positions[i]);
      } else {
        groups.push(currentGroup);
        currentGroup = [positions[i]];
      }
    }
    groups.push(currentGroup);

    return groups
      .map((group) => {
        if (group.length === 1) return group[0].toString();
        return `${group[0]}-${group[group.length - 1]}`;
      })
      .join(", ");
  }
}

const DivisionSplittingRulesField: React.FC = () => {
  const controller = DivisionSplittingRulesController.use();
  const { rules, divisions, qualifiersPerGroup } = controller.state;

  const allPositions = new Set(rules.flatMap((r) => r.positions));
  const availablePositions = Array.from({ length: qualifiersPerGroup }, (_, i) => i + 1).filter(
    (p) => !allPositions.has(p),
  );

  // Validation
  const validationErrors: string[] = [];
  const positionToDivision = new Map<number, number>();
  rules.forEach((rule) => {
    rule.positions.forEach((pos) => {
      if (pos < 1 || pos > qualifiersPerGroup) {
        validationErrors.push(`Position ${pos} is out of range (1-${qualifiersPerGroup})`);
      }
      if (positionToDivision.has(pos)) {
        validationErrors.push(`Position ${pos} is assigned to multiple divisions`);
      }
      positionToDivision.set(pos, rule.division);
    });
    if (rule.division < 1 || rule.division > divisions) {
      validationErrors.push(`Division ${rule.division} is out of range (1-${divisions})`);
    }
  });

  const wwwUrl = import.meta.env.VITE_WWW || "https://www.mixitone.com";

  return (
    <div className="space-y-4">
      <div className="text-sm text-gray-600">
        Configure how group positions are assigned to divisions. For example: top player → Division 1, 2nd
        player → Division 2, 3rd-4th players → Division 3.{" "}
        <a
          href={`${wwwUrl}/docs/tournaments/formats/round-robin#playoff-divisions`}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 underline hover:text-blue-800"
        >
          Learn more about division splitting
        </a>
      </div>
      {validationErrors.length > 0 && (
        <div className="p-3 border border-red-200 rounded-lg bg-red-50">
          <div className="mb-1 text-sm font-medium text-red-800">Validation Errors:</div>
          <ul className="text-sm text-red-700 list-disc list-inside">
            {validationErrors.map((error, idx) => (
              <li key={idx}>{error}</li>
            ))}
          </ul>
        </div>
      )}
      {rules.length === 0 && (
        <div className="text-sm italic text-gray-500">
          No rules configured. Players will be distributed evenly.
        </div>
      )}
      {rules.length > 0 && (
        <div className="border border-gray-200 divide-y divide-gray-200 rounded-lg">
          {rules.map((rule, index) => (
            <div key={index} className="space-y-1.5 px-3 py-2">
              <div className="flex items-end gap-2.5">
                <div className="flex-1">
                  <label
                    htmlFor={`division-rules-positions-${index}`}
                    className="block text-xs font-medium text-gray-700 mb-0.5"
                  >
                    Positions
                  </label>
                  <Input
                    id={`division-rules-positions-${index}`}
                    type="text"
                    value={controller.getPositionsDisplay(rule.positions)}
                    onChange={(e) => controller.actionUpdatePositions(index, e.target.value)}
                    placeholder="e.g., 1 or 1,2,3 or 3-4"
                    className="w-full"
                  />
                </div>
                <div className="w-40">
                  <label
                    htmlFor={`division-rules-division-${index}`}
                    className="block text-xs font-medium text-gray-700 mb-0.5"
                  >
                    Division
                  </label>
                  <Select
                    id={`division-rules-division-${index}`}
                    value={rule.division.toString()}
                    onChange={(e) =>
                      controller.actionUpdateRule(index, { division: Number.parseInt(e.target.value, 10) })
                    }
                  >
                    {Array.from({ length: divisions }, (_, i) => i + 1).map((div) => (
                      <option key={div} value={div.toString()}>
                        Division {div}
                      </option>
                    ))}
                  </Select>
                </div>
                <div>
                  <Button type="button" kind="danger" onClick={() => controller.actionRemoveRule(index)}>
                    Remove
                  </Button>
                </div>
              </div>
              <div className="text-[11px] leading-snug text-gray-500">
                Enter positions (1-{qualifiersPerGroup}) as numbers, comma-separated, or ranges (e.g., "3-4")
              </div>
            </div>
          ))}
        </div>
      )}
      <Button type="button" kind="secondary" onClick={() => controller.actionAddRule()}>
        Add rule
      </Button>
      {availablePositions.length > 0 && (
        <div className="text-xs text-amber-600">Unassigned positions: {availablePositions.join(", ")}</div>
      )}
    </div>
  );
};

export default DivisionSplittingRulesController.scope(ApplicationView(DivisionSplittingRulesField));
